import { z } from "zod";

import type { PublicProOffer } from "@/hooks/use-public-pro-offers";

export const checkoutEmailSchema = z
	.string()
	.trim()
	.min(1, "Enter your email address.")
	.max(254, "Email is too long.")
	.email("Enter a valid email address.");

export type CheckoutFieldErrors = {
	email?: string;
	trial?: string;
	yearly?: string;
};

export function getCheckoutFieldErrors({
	email,
	isLoggedIn,
	selectedOffer,
	trialAccepted,
	yearlyAccepted,
	requiresTrialAcceptance,
	requiresMonthlyPlanAcceptance,
}: {
	email: string;
	isLoggedIn: boolean;
	selectedOffer: PublicProOffer | null;
	trialAccepted: boolean;
	yearlyAccepted: boolean;
	requiresTrialAcceptance: boolean;
	requiresMonthlyPlanAcceptance: boolean;
}): CheckoutFieldErrors {
	const next: CheckoutFieldErrors = {};

	if (!isLoggedIn) {
		const parsed = checkoutEmailSchema.safeParse(email);
		if (!parsed.success) {
			next.email = parsed.error.issues[0]?.message ?? "Invalid email.";
		}
	}

	if (!selectedOffer) {
		return next;
	}

	if (requiresMonthlyPlanAcceptance && !trialAccepted) {
		next.trial = requiresTrialAcceptance
			? "Please confirm the 7-day trial to continue with the monthly plan."
			: "Please confirm the monthly plan billing terms to continue.";
	}

	if (selectedOffer.recurringInterval === "year" && !yearlyAccepted) {
		next.yearly = "Check the box to accept the yearly plan billing terms.";
	}

	return next;
}

export const CHECKOUT_ERROR_SUMMARY_ORDER = [
	"email",
	"trial",
	"yearly",
] as const satisfies readonly (keyof CheckoutFieldErrors)[];

export function getCheckoutErrorSummaryItems(errors: CheckoutFieldErrors) {
	return CHECKOUT_ERROR_SUMMARY_ORDER.flatMap((key) => {
		const message = errors[key];
		return message ? [{ key, message }] : [];
	});
}
